// Add candidate (`#candidates?action=add[&method=upload|manual|reuse][&req=<R>]`, DS DC-82 / candidate C-5): his three
// creation methods as tiles — "V1 creation methods: resume upload with parsing and Recruiter review; manual candidate
// entry; and reuse of an existing talent-community or historical candidate profile." Manual entry asks the four
// required fields and offers the documented no-email exception for a sourced candidate; Save runs the potential-matches
// gate before anything is written. Manual entry starts from his internal/external flag, *Internal | External*: Internal
// records the internal source (V1's internal signal, so the profile reads Internal), External offers the other sources.
// Resume upload stages the PDF or DOCX, starts the parse and opens Review parsed values; an invalid, password-protected
// or unreadable file is a clear error and creates nothing. Reuse searches the profiles this seat may see and ends in
// *Link to requisition* ("A new requisition application links to the existing Candidate ID rather than duplicating the
// person profile."): the chosen profile (`&token=` or `&ref=`) opens the link dialog. Every state opens from the URL, so
// a capture frame can show it. Manual entry's Save is live: an attempt with a required field missing marks each one
// *Required* in place and writes nothing; Primary email carries its tag as the footer rule reads ("primary email when
// available"), the rule is said once, in the footer, and an empty Source looks like an empty Owner (UAT F-74).

import { useId, useState } from "react";
import { AppError, uploadFile } from "@/api/runtime";
import type { CandidateCreateOutput, SearchRow } from "@/api/tools";
import { candidateParseResume, candidateSearch } from "@/api/tools";
import { useRead } from "@/api/useTool";
import { go } from "@/router";
import { useSession } from "@/session/useSession";
import {
  ACTION_LABELS, Banner, Button, CheckTile, COMMON_LABELS, Dialog, DIALOG_LABELS, DropZone, ErrorStrip, Field, FilterBar, Flex, G1_LABELS,
  Grid, IdPill, Input, PersonPick, RadioTile, RULE_LABELS, Segmented, Select, Stack, Table, Td, TextArea, TextLink, Tr, type DropZoneError,
} from "@/mc";
import { BRD } from "./brd";
import { usePeopleChoices, useSourceChoices, type Choice } from "./options";
import { withQuery } from "@/views/shared/settledRead";
import { CANDIDATE_LABELS as L, CANDIDATE_RULES } from "./labels";
import { PotentialMatches, type Entry } from "./PotentialMatches";
import { LinkDialog } from "./ProfileDialogs";
import { refusalSentence, ruleCode } from "./refusals";
import { gateBlocked, useSaveGate } from "./save";

export interface Saved {
  candidateId: string;
  candidateNo: string;
}

type Method = "upload" | "manual" | "reuse";
const METHODS: readonly Method[] = ["upload", "manual", "reuse"];

/** A seat that decides a potential match itself (BRD 10.1 "an authorized Recruiter or TA user": the Recruiter, the TA
 *  Manager or the Admin, DC-228); a Coordinator refers. */
export function mayResolveMatches(roles: readonly string[]): boolean {
  return roles.some((role) => role === "recruiter" || role === "ta_manager" || role === "admin");
}

/** The owner picker: a Recruiter owns what they add unless they name another; anyone else names one. The choices are
 *  the active Recruiters (`people.options`); the server still refuses any other owner (`owner_not_recruiter`). */
export function useOwnerChoices() {
  const { me } = useSession();
  const recruiter = Boolean(me?.roles.includes("recruiter"));
  const { choices, isLoading } = usePeopleChoices("recruiter");
  return { recruiter, self: me?.person_id ?? "", choices, loading: isLoading };
}

export function AddCandidateDialog({ flags, onClose, onSaved }: {
  flags: URLSearchParams;
  onClose: () => void;
  onSaved: (saved: Saved) => void;
}) {
  const { can, me } = useSession();
  const method = METHODS.find((value) => value === flags.get("method")) ?? null;
  const requisitionId = flags.get("req");
  const pick = (value: string) => go(withQuery("#candidates", { method: value }, flags));
  const reused = method === "reuse" ? { token: flags.get("token"), ref: flags.get("token") ? null : flags.get("ref") } : null;
  if (reused && (reused.token || reused.ref) && can("candidate.link_to_requisition")) {
    // the reused profile ends in Link to requisition; closing it goes back to the search
    const back = () => go(withQuery("#candidates", { token: null, ref: null }, flags));
    return (
      <LinkDialog
        subject={reused}
        roles={me?.roles ?? []}
        onClose={back}
        onLinked={(out) => (out.candidate_id ? onSaved({ candidateId: out.candidate_id, candidateNo: out.candidate_no }) : back())}
      />
    );
  }
  const chooser = (
    <RadioTile
      label={ACTION_LABELS.create}
      value={method ?? undefined}
      onChange={pick}
      options={[
        { value: "upload", label: L.resumeUpload, hs: "cand.add.upload" },
        { value: "manual", label: L.manualCandidateEntry, hs: "cand.add.manual" },
        { value: "reuse", label: L.reuseAnExistingProfile, hs: "cand.add.reuse" },
      ]}
    />
  );
  if (method === "manual") return <ManualEntry chooser={chooser} requisitionId={requisitionId} onClose={onClose} onSaved={onSaved} />;
  return (
    <Dialog preset="addCandidate" width={640} onClose={onClose} dismissLabel={ACTION_LABELS.cancel} >
      <Stack gap={16}>
        {chooser}
        {method === "upload" ? <ResumeUpload requisitionId={requisitionId} /> : null}
        {method === "reuse" ? <Reuse onLink={(row) => go(withQuery("#candidates", row.candidate_id ? { token: row.candidate_id, ref: null } : { ref: row.ref, token: null }, flags))} /> : null}
      </Stack>
    </Dialog>
  );
}

// ---- Resume upload ----------------------------------------------------------------------------------------------

/** The DropZone's clear error for a refused upload or a failed parse (BRD 10.3's three causes). */
export function fileError(cause: string | null | undefined): DropZoneError {
  if (cause === "password_protected") return "passwordProtected";
  if (cause === "unreadable" || cause === "timeout" || cause === "infected" || cause === "malware") return "unreadable";
  return "invalid";
}

function ResumeUpload({ requisitionId }: { requisitionId: string | null }) {
  const { me } = useSession();
  const owner = useOwnerChoices();
  const [ownerId, setOwnerId] = useState("");
  const [name, setName] = useState<string | undefined>();
  const [progress, setProgress] = useState<number | undefined>();
  const [error, setError] = useState<DropZoneError | undefined>();
  const [refused, setRefused] = useState<string | null>(null);
  const [failure, setFailure] = useState<AppError | null>(null);
  const ownerOf = owner.recruiter ? (ownerId || owner.self) : ownerId;

  const onFile = async (file: File) => {
    setName(file.name);
    setError(undefined);
    setRefused(null);
    setFailure(null);
    if (!/\.(pdf|docx)$/i.test(file.name)) {
      setError("invalid");
      return;
    }
    setProgress(10);
    try {
      const staged = await uploadFile(file, { owner_type: "import", owner_id: me?.person_id ?? "", kind: "resume", requisition_id: requisitionId }, {
        onPhase: (phase) => setProgress(phase === "uploading" ? 30 : 70),
      });
      const parse = await candidateParseResume({ file_id: staged.file_id, owner_id: ownerOf || null, requisition_id: requisitionId });
      setProgress(100);
      go(`#candidates/review/${parse.parse_id}`);
    } catch (caught) {
      setProgress(undefined);
      if (!(caught instanceof AppError)) throw caught;
      if (caught.status >= 500 || caught.code === "NETWORK") setFailure(caught);
      // The door's own refusals (415/413) and the resume rule (a resume is a PDF or a DOCX, by content): the zone's error.
      else if (caught.rejectReason || caught.status === 415 || caught.status === 413 || ruleCode(caught) === "resume_type") setError(fileError(caught.documentError ?? caught.rejectReason));
      else if (ruleCode(caught) === "parse_open" && caught.fieldErrors.file_id) go(`#candidates/review/${caught.fieldErrors.file_id}`); // already being reviewed: open that review
      else setRefused(refusalSentence(caught));
    }
  };

  return (
    <Stack gap={12}>
      {refused ? <Banner tone="red" live>{refused}</Banner> : null}
      {failure ? <ErrorStrip message={RULE_LABELS.failedSyncRetryable} problem={{ status: failure.status, refusal_code: failure.code, request_id: failure.requestId }} /> : null}
      <Field label={L.owner} required={!owner.recruiter} hint={CANDIDATE_RULES.recruiterCreates}>
        <PersonPick
          options={owner.choices.map((choice) => ({ value: choice.value, name: choice.label }))}
          value={ownerOf || undefined}
          onChange={setOwnerId}
          loading={owner.loading}
          
        />
      </Field>
      <DropZone
        required
        onFile={(file) => { void onFile(file); }}
        fileName={name}
        progress={progress}
        error={error}
        blocked={!ownerOf ? { reason: CANDIDATE_RULES.manualEntry } : undefined}
        
      />
    </Stack>
  );
}

// ---- Reuse an existing profile ----------------------------------------------------------------------------------

function Reuse({ onLink }: { onLink: (row: SearchRow) => void }) {
  const { can } = useSession();
  const [q, setQ] = useState("");
  const term = q.trim();
  const found = useRead(["candidate-search", "reuse", term], () => candidateSearch({ q: term, limit: 20 }), { enabled: term.length >= 2 });
  return (
    <Stack gap={12}>
      <FilterBar label={L.reuseAnExistingProfile} search={{ value: q, onChange: setQ, hs: "cand.reuse.search" }} />
      {term.length >= 2 ? (
        <ReuseResults rows={found.data?.rows ?? []} loading={found.isLoading} ready={Boolean(found.data)} mayLink={can("candidate.link_to_requisition")} onLink={onLink} />
      ) : null}
    </Stack>
  );
}

/** The profiles a reuse may take (never a masked one): each opens its profile and, for a seat that may link, ends in
 *  Link to requisition — the new application under the same Candidate ID. */
export function ReuseResults({ rows, loading, ready, mayLink, onLink }: {
  rows: readonly SearchRow[];
  loading: boolean;
  ready: boolean;
  mayLink: boolean;
  onLink: (row: SearchRow) => void;
}) {
  const shown = rows.filter((row) => row.projection !== "masked");
  return (
    <Table dense label={L.reuseAnExistingProfile} loading={loading ? 3 : undefined} empty={ready && shown.length === 0 ? "—" : undefined} cols={3}>
      {shown.map((row, index) => (
        <Tr key={row.ref} >
          <Td label={COMMON_LABELS.candidateId}><IdPill value={row.candidate_no} /></Td>
          <Td label={L.name}>
            {row.candidate_id ? <TextLink href={`#candidates/${row.candidate_id}`} >{row.display_name ?? row.candidate_no}</TextLink> : row.display_name}
          </Td>
          <Td label={L.owner}>
            <Flex gap={8} align="center" justify="between">
              <span>{row.owner_name ?? "—"}</span>
              {mayLink && row.status !== "Merged" ? (
                <Button tone="ghost" sm onClick={() => onLink(row)} >{DIALOG_LABELS.linkToRequisition}</Button>
              ) : null}
            </Flex>
          </Td>
        </Tr>
      ))}
    </Table>
  );
}

// ---- Manual candidate entry -------------------------------------------------------------------------------------

export type CandidateKindValue = "internal" | "external";

/** The sources the choice allows: Internal is the internal source alone; External is every other source. */
export function sourcesFor(kind: CandidateKindValue, choices: readonly Choice[]): Choice[] {
  return choices.filter((choice) => (choice.value === "internal") === (kind === "internal"));
}

/** His internal/external flag as the first choice of manual entry: *Internal | External*. */
export function CandidateKind({ value, onChange }: { value: CandidateKindValue; onChange: (value: CandidateKindValue) => void }) {
  const id = `${useId()}-kind`;
  return (
    <Field label={L.internalExternal} id={id}>
      <Segmented<CandidateKindValue>
        aria-labelledby={`${id}-l`}
        options={[
          { value: "internal", label: L.internal, hs: "cand.add.internal" },
          { value: "external", label: L.external, hs: "cand.add.external" },
        ]}
        value={value}
        onChange={onChange}
      />
    </Field>
  );
}

export interface Form extends Entry {
  location: string;
  currentEmployer: string;
  kind: CandidateKindValue;
  source: string;
  owner: string;
  noEmail: boolean;
  noEmailReason: string;
}

export const EMPTY_FORM: Form = {
  name: "", email: "", secondaryEmail: "", phone: "", linkedinUrl: "", location: "", currentEmployer: "", kind: "external", source: "", owner: "",
  noEmail: false, noEmailReason: "",
};

/** The required fields a Save would leave missing, by the server's field names: the name, the primary email (unless a
 *  sourced candidate takes the documented no-email exception, which then needs its reason), the source and the owner
 *  ("Required manual-entry fields are name, primary email when available, source, and owner."). */
export function missingFields(form: Form, ownerOf: string): string[] {
  const exception = !form.email.trim() && form.noEmail && form.source === "sourced";
  return [
    form.name.trim() ? null : "display_name",
    form.email.trim() || exception ? null : "email",
    exception && form.noEmailReason.trim().length < 3 ? "no_email_reason" : null,
    form.source ? null : "source_channel",
    ownerOf ? null : "owner_id",
  ].filter((field): field is string => field !== null);
}

/** What a Save attempt marks: each missing required field reads *Required*. */
export function attemptErrors(missing: readonly string[]): Record<string, string> {
  return Object.fromEntries(missing.map((field) => [field, COMMON_LABELS.required]));
}

/** A field's error line from the refusal's field errors: *Required*, or *invalid* for a value the server rejected. */
export function fieldError(errors: Record<string, string>, field: string): string | undefined {
  const value = errors[field];
  if (value === undefined) return undefined;
  return value === "invalid" ? G1_LABELS.fileInvalid : COMMON_LABELS.required;
}

function ManualEntry({ chooser, requisitionId, onClose, onSaved }: {
  chooser: React.ReactNode;
  requisitionId: string | null;
  onClose: () => void;
  onSaved: (saved: Saved) => void;
}) {
  const { me } = useSession();
  const owner = useOwnerChoices();
  const sourceChoices = useSourceChoices();
  const mayResolve = mayResolveMatches(me?.roles ?? []);
  const [form, setForm] = useState<Form>(EMPTY_FORM);
  const [tried, setTried] = useState(false);
  const set = <K extends keyof Form>(key: K, value: Form[K]) => setForm((prev) => ({ ...prev, [key]: value }));
  const sources = sourcesFor(form.kind, sourceChoices);
  // Internal is the internal source; switching back to External clears it
  const choose = (kind: CandidateKindValue) => setForm((prev) => ({ ...prev, kind, source: kind === "internal" ? "internal" : prev.source === "internal" ? "" : prev.source }));
  const ownerOf = owner.recruiter ? (form.owner || owner.self) : form.owner;
  const entry: Entry = { name: form.name, email: form.email, secondaryEmail: form.secondaryEmail, phone: form.phone, linkedinUrl: form.linkedinUrl };
  const gate = useSaveGate({
    entry,
    requisitionId,
    mayResolve,
    build: () => ({
      display_name: form.name.trim(),
      source_channel: form.source,
      owner_id: form.owner || null,
      email: form.email.trim() || null,
      secondary_email: form.secondaryEmail.trim() || null,
      phone: form.phone.trim() || null,
      linkedin_url: form.linkedinUrl.trim() || null,
      location: form.location.trim() || null,
      current_employer: form.currentEmployer.trim() || null,
      no_email_reason: !form.email.trim() && form.noEmail ? form.noEmailReason.trim() : null,
      requisition_id: requisitionId,
    }),
    onSaved: (out: CandidateCreateOutput) => onSaved({ candidateId: out.candidate_id, candidateNo: out.candidate_no }),
  });

  const sourced = form.source === "sourced";
  const emailMissing = !form.email.trim();
  const missing = missingFields(form, ownerOf);
  // the footer says the rule; a Save attempt with a field missing marks it in place instead of a blocked, silent Save
  const blocked = missing.length === 0 && gateBlocked(gate, mayResolve) ? { reason: CANDIDATE_RULES.potentialMatch } : undefined;
  const marks: Record<string, string> = tried ? attemptErrors(missing) : {};
  const errorOf = (field: string) => marks[field] ?? fieldError(gate.fieldErrors, field);

  return (
    <Dialog
      preset="addCandidate"
      width={720}
      onClose={onClose}
      dismissLabel={ACTION_LABELS.cancel}
      
      refused={gate.refused && gate.hits === null ? { reason: gate.refused } : undefined}
      hint={CANDIDATE_RULES.manualEntry}
      primary={{
        label: ACTION_LABELS.save,
        onClick: () => {
          setTried(true);
          if (missing.length === 0) void gate.save();
        },
        blocked,
        busy: gate.busy,
        hs: "cand.add.save",
      }}
    >
      <Stack gap={16}>
        {chooser}
        {gate.failure ? (
          <ErrorStrip message={RULE_LABELS.failedSyncRetryable} problem={{ status: gate.failure.status, refusal_code: gate.failure.code, request_id: gate.failure.requestId }} onRetry={() => { void gate.save(); }} />
        ) : null}
        <CandidateKind value={form.kind} onChange={choose} />
        <Grid cols={2} min={240}>
          <Field label={L.name} required error={errorOf("display_name")}>
            <Input value={form.name} onChange={(event) => set("name", event.target.value)} autoComplete="off"  />
          </Field>
          <Field label={L.primaryEmail} required={!(form.noEmail && sourced)} optional={form.noEmail && sourced} error={errorOf("email")}>
            <Input type="email" value={form.email} onChange={(event) => set("email", event.target.value)} autoComplete="off"  />
          </Field>
          <Field label={L.source} required error={errorOf("source_channel")}>
            <Select options={sources} value={form.source} onChange={(value) => set("source", value)} placeholder=""  />
          </Field>
          <Field label={L.owner} required={!owner.recruiter} optional={owner.recruiter} error={errorOf("owner_id")}>
            <PersonPick
              options={owner.choices.map((choice) => ({ value: choice.value, name: choice.label }))}
              value={ownerOf || undefined}
              onChange={(value) => set("owner", value)}
              loading={owner.loading}
              
            />
          </Field>
          <Field label={L.secondaryEmail} optional error={errorOf("secondary_email")}>
            <Input type="email" value={form.secondaryEmail} onChange={(event) => set("secondaryEmail", event.target.value)} autoComplete="off"  />
          </Field>
          <Field label={L.phone} optional error={errorOf("phone")}>
            <Input type="tel" value={form.phone} onChange={(event) => set("phone", event.target.value)} autoComplete="off"  />
          </Field>
          <Field label={L.linkedinUrl} optional error={errorOf("linkedin_url")}>
            <Input value={form.linkedinUrl} onChange={(event) => set("linkedinUrl", event.target.value)} autoComplete="off"  />
          </Field>
          <Field label={L.location} optional>
            <Input value={form.location} onChange={(event) => set("location", event.target.value)} autoComplete="off"  />
          </Field>
          <Field label={BRD.currentEmployer} optional>
            <Input value={form.currentEmployer} onChange={(event) => set("currentEmployer", event.target.value)} autoComplete="off"  />
          </Field>
        </Grid>
        {emailMissing && sourced ? (
          <Stack gap={8}>
            <CheckTile checked={form.noEmail} onChange={(on) => set("noEmail", on)} sub={CANDIDATE_RULES.noEmail} >
              {L.noEmailException}
            </CheckTile>
            {form.noEmail ? (
              <Field label={G1_LABELS.reason} required hintTone="flag" hint={CANDIDATE_RULES.noEmail} error={errorOf("no_email_reason")}>
                <TextArea rows={2} value={form.noEmailReason} onChange={(event) => set("noEmailReason", event.target.value)}  />
              </Field>
            ) : null}
          </Stack>
        ) : null}
        {gate.hits !== null && gate.hits.length > 0 ? (
          <PotentialMatches
            hits={gate.hits}
            entry={entry}
            mayResolve={mayResolve}
            requisitionId={requisitionId}
            onUseExisting={(hit) => { void gate.useExisting(hit); }}
            onOpenProfile={(id) => go(`#candidates/${id}`)}
            busy={gate.busy}
            distinct={gate.distinct}
            onDistinct={gate.setDistinct}
            reason={gate.reason}
            onReason={gate.setReason}
            refused={gate.refused}
          />
        ) : null}
      </Stack>
    </Dialog>
  );
}
